import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createClient, updateClient, fetchClient, clearClient } from '../store/slices/clientsSlice';
import { toast } from 'react-toastify';
import { ArrowLeft, Save, User, Phone, Mail, Calendar, Briefcase, FileText, ShieldCheck } from 'lucide-react';

/* ── Styles locaux compacts ── */
const S = {
  card:    { margin: 0, padding: '10px 14px', display: 'flex', flexDirection: 'column', minHeight: 0 },
  head:    { display: 'flex', alignItems: 'center', gap: 7, paddingBottom: 8,
             borderBottom: '1px solid #E5E7EB', marginBottom: 10, flexShrink: 0 },
  htitle:  { fontSize: 11, fontWeight: 700, color: '#1F4E79', margin: 0,
             textTransform: 'uppercase', letterSpacing: '0.05em' },
  label:   { display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 },
  grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  fg:      { margin: 0 },
  err:     { color: '#C00000', fontSize: 10, marginTop: 2 },
};

function SHead({ icon: Icon, title, color = '#1F4E79' }) {
  return (
    <div style={S.head}>
      <Icon size={13} color={color} />
      <span style={{ ...S.htitle, color }}>{title}</span>
    </div>
  );
}

export default function ClientFormPage() {
  const { id }   = useParams();
  const isEdit   = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { client, loading } = useSelector(s => s.clients);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      nom_cli: '', prenom_cli: '', nom_jeune_fille: '', sexe_cli: '',
      date_naissance_cli: '',
      date_inscription_cli: new Date().toISOString().slice(0, 10),
      telephone_principal: '', email_cli: '', fonction: '',
      statut_cli: 'actif', type_identite: '', numero_identite: '', note_interne: '',
    },
  });

  useEffect(() => {
    if (isEdit) dispatch(fetchClient(id));
    return () => { dispatch(clearClient()); };
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (isEdit && client) {
      reset({
        nom_cli:              client.nom_cli || '',
        prenom_cli:           client.prenom_cli || '',
        nom_jeune_fille:      client.nom_jeune_fille || '',
        sexe_cli:             client.sexe_cli || '',
        date_naissance_cli:   client.date_naissance_cli || '',
        date_inscription_cli: client.date_inscription_cli || new Date().toISOString().slice(0, 10),
        telephone_principal:  client.telephone_principal || '',
        email_cli:            client.email_cli || '',
        fonction:             client.fonction || '',
        statut_cli:           client.statut_cli || 'actif',
        type_identite:        client.type_identite || '',
        numero_identite:      client.numero_identite || '',
        note_interne:         client.note_interne || '',
      });
    }
  }, [client?.id, isEdit]);

  const onSubmit = async (data) => {
    const payload = { ...data };
    if (!payload.email_cli)            delete payload.email_cli;
    if (!payload.date_naissance_cli)   delete payload.date_naissance_cli;
    if (!payload.date_inscription_cli) delete payload.date_inscription_cli;
    if (!payload.nom_jeune_fille)      payload.nom_jeune_fille = '';
    if (!payload.fonction)             payload.fonction = '';
    if (!payload.note_interne)         payload.note_interne = '';
    delete payload.commercial_ref;
    try {
      if (isEdit) {
        await dispatch(updateClient({ id, payload })).unwrap();
      } else {
        await dispatch(createClient(payload)).unwrap();
      }
      toast.success(isEdit ? 'Client modifié avec succès.' : 'Client créé avec succès.');
      navigate('/clients');
    } catch (err) {
      if (err && typeof err === 'object') {
        const msgs = Object.values(err).flat()
          .map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join(' | ');
        toast.error(msgs || "Erreur lors de l'enregistrement.");
      } else {
        toast.error("Erreur lors de l'enregistrement.");
      }
    }
  };

  if (isEdit && loading && !client) {
    return (
      <div className="spinner" style={{ marginTop: 60 }}>
        <div className="spinner-ring" /> Chargement du client...
      </div>
    );
  }

  return (
    /* Remplir tout l'espace du main-content sans scroll */
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Barre titre ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexShrink: 0 }}>
        <button className="header-btn btn-secondary btn-sm"
          onClick={() => navigate('/clients')}
          style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <ArrowLeft size={13} /> Retour
        </button>
        <div>
          <h1 style={{ fontSize: 16, fontWeight: 700, color: '#1F2937', margin: 0 }}>
            {isEdit ? 'Modifier le client' : 'Nouveau client'}
          </h1>
          {isEdit && client && (
            <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>
              {client.prenom_cli} {client.nom_cli}
            </p>
          )}
        </div>
      </div>

      {/* ── Formulaire ── */}
      <form onSubmit={handleSubmit(onSubmit)}
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* ══ Grille 2 colonnes — prend tout l'espace restant ══ */}
        <div style={{ flex: 1, minHeight: 0, display: 'grid',
          gridTemplateColumns: '1fr 1fr', gap: 12, overflow: 'hidden' }}>

          {/* ── COL 1 : IDENTITÉ ── */}
          <div className="card" style={{ ...S.card, overflow: 'hidden' }}>
            <SHead icon={User} title="Identité" />
            <div style={{ ...S.grid2, flex: 1, alignContent: 'start' }}>

              <div style={S.fg}>
                <label style={S.label}>Nom <span style={{ color: '#C00000' }}>*</span></label>
                <input className={`form-control ${errors.nom_cli ? 'error' : ''}`}
                  style={{ fontSize: 12, padding: '6px 10px' }}
                  placeholder="Dupont"
                  {...register('nom_cli', { required: 'Obligatoire' })} />
                {errors.nom_cli && <p style={S.err}>{errors.nom_cli.message}</p>}
              </div>

              <div style={S.fg}>
                <label style={S.label}>Prénom <span style={{ color: '#C00000' }}>*</span></label>
                <input className={`form-control ${errors.prenom_cli ? 'error' : ''}`}
                  style={{ fontSize: 12, padding: '6px 10px' }}
                  placeholder="Marie"
                  {...register('prenom_cli', { required: 'Obligatoire' })} />
                {errors.prenom_cli && <p style={S.err}>{errors.prenom_cli.message}</p>}
              </div>

              <div style={S.fg}>
                <label style={S.label}>Nom de jeune fille</label>
                <input className="form-control" style={{ fontSize: 12, padding: '6px 10px' }}
                  placeholder="Si applicable"
                  {...register('nom_jeune_fille')} />
              </div>

              <div style={S.fg}>
                <label style={S.label}>Sexe <span style={{ color: '#C00000' }}>*</span></label>
                <select className={`form-control ${errors.sexe_cli ? 'error' : ''}`}
                  style={{ fontSize: 12, padding: '6px 10px' }}
                  {...register('sexe_cli', { required: 'Obligatoire' })}>
                  <option value="">— Sélectionner —</option>
                  <option value="masculin">Masculin</option>
                  <option value="feminin">Féminin</option>
                </select>
                {errors.sexe_cli && <p style={S.err}>{errors.sexe_cli.message}</p>}
              </div>

              <div style={S.fg}>
                <label style={S.label}>
                  <Calendar size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                  Date de naissance
                </label>
                <input type="date" className="form-control"
                  style={{ fontSize: 12, padding: '6px 10px' }}
                  {...register('date_naissance_cli')} />
              </div>

              <div style={S.fg}>
                <label style={S.label}>
                  <Calendar size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                  Date d'inscription <span style={{ color: '#C00000' }}>*</span>
                </label>
                <input type="date"
                  className={`form-control ${errors.date_inscription_cli ? 'error' : ''}`}
                  style={{ fontSize: 12, padding: '6px 10px' }}
                  {...register('date_inscription_cli', { required: 'Obligatoire' })} />
                {errors.date_inscription_cli && <p style={S.err}>{errors.date_inscription_cli.message}</p>}
              </div>

              <div style={{ ...S.fg, gridColumn: '1 / -1' }}>
                <label style={S.label}>Statut</label>
                <select className="form-control" style={{ fontSize: 12, padding: '6px 10px' }}
                  {...register('statut_cli')}>
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                  <option value="archive">Archivé</option>
                </select>
              </div>

            </div>
          </div>

          {/* ── COL 2 : 3 cartes empilées ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, overflow: 'hidden' }}>

            {/* Ligne 1 : Document */}
            <div className="card" style={{ ...S.card, flexShrink: 0 }}>
              <SHead icon={ShieldCheck} title="Document d'identité" />
              <div style={S.grid2}>
                <div style={S.fg}>
                  <label style={S.label}>Type de pièce</label>
                  <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                    {[{ value: 'cni', label: 'CNI' }, { value: 'passeport', label: 'Passeport' }].map(({ value, label }) => (
                      <label key={value} style={{
                        display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                        border: '1.5px solid #E5E7EB', userSelect: 'none', background: 'white',
                      }}>
                        <input type="radio" value={value}
                          {...register('type_identite')}
                          style={{ accentColor: '#1F4E79', width: 12, height: 12 }} />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Numéro de la pièce</label>
                  <input className="form-control" style={{ fontSize: 12, padding: '6px 10px' }}
                    placeholder="Ex : A123456…"
                    {...register('numero_identite')} />
                </div>
              </div>
            </div>

            {/* Ligne 2 : Coordonnées & Profil */}
            <div className="card" style={{ ...S.card, flexShrink: 0 }}>
              <SHead icon={Phone} title="Coordonnées & Profil" />
              <div style={S.grid2}>

                <div style={S.fg}>
                  <label style={S.label}>
                    <Phone size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                    Téléphone <span style={{ color: '#C00000' }}>*</span>
                  </label>
                  <input className={`form-control ${errors.telephone_principal ? 'error' : ''}`}
                    style={{ fontSize: 12, padding: '6px 10px' }}
                    placeholder="+243 81 000 0000"
                    {...register('telephone_principal', {
                      required: 'Obligatoire',
                      minLength: { value: 6, message: 'Numéro trop court' },
                    })} />
                  {errors.telephone_principal && <p style={S.err}>{errors.telephone_principal.message}</p>}
                </div>

                <div style={S.fg}>
                  <label style={S.label}>
                    <Mail size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                    Adresse email
                  </label>
                  <input type="email"
                    className={`form-control ${errors.email_cli ? 'error' : ''}`}
                    style={{ fontSize: 12, padding: '6px 10px' }}
                    placeholder="client@exemple.com"
                    {...register('email_cli', {
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email invalide' },
                    })} />
                  {errors.email_cli && <p style={S.err}>{errors.email_cli.message}</p>}
                </div>

                <div style={{ ...S.fg, gridColumn: '1 / -1' }}>
                  <label style={S.label}>
                    <Briefcase size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                    Fonction / Poste
                  </label>
                  <input className="form-control" style={{ fontSize: 12, padding: '6px 10px' }}
                    placeholder="Ex : Directeur commercial, Ingénieur…"
                    {...register('fonction')} />
                </div>

              </div>
            </div>

            {/* Ligne 3 : Notes — prend tout l'espace restant */}
            <div className="card" style={{ ...S.card, flex: 1 }}>
              <SHead icon={FileText} title="Notes internes" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <label style={S.label}>Remarques / Observations</label>
                <textarea className="form-control"
                  style={{ flex: 1, resize: 'none', fontSize: 12, padding: '6px 10px', minHeight: 0 }}
                  placeholder="Informations complémentaires visibles uniquement en interne…"
                  {...register('note_interne')} />
              </div>
            </div>

          </div>
        </div>

        {/* ── Boutons ── */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end',
          marginTop: 10, flexShrink: 0 }}>
          <button type="button" className="header-btn btn-secondary"
            onClick={() => navigate('/clients')}
            style={{ padding: '8px 20px', fontSize: 12 }}>
            Annuler
          </button>
          <button type="submit" className="header-btn btn-primary" disabled={isSubmitting}
            style={{ padding: '8px 24px', fontSize: 12, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 6 }}>
            <Save size={13} />
            {isSubmitting
              ? (isEdit ? 'Enregistrement...' : 'Création...')
              : (isEdit ? 'Enregistrer les modifications' : 'Créer le client')}
          </button>
        </div>

      </form>
    </div>
  );
}
